import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { refreshEstimateExportCache, fetchEstimateExportUncached } from "../../api/estimates";
import { clearProcessedEstimateCache } from "../../excel/datasources/estimate";
import type { AggregatedQto } from "../../integrations/qto/types";
import { exportReviewToEstimate } from "../../excel/takeoff/qtoReview";
import { reviewQTO } from "../../excel/takeoff/reviewSheet";
import { countQtoReviewRows, type EstimateLineItemForReview } from "../../integrations/qto/reviewRow";
import type { LineItemsCsvOptions } from "../../integrations/qto/reviewCsv";
import Logger from "../../clients/sentry";
import { handleError } from "../../errors";
import { track } from "../../clients/mixpanel";
import { LoggingInfo } from "./types";

type SubmitSuccessData = NonNullable<
  Extract<Awaited<ReturnType<typeof exportReviewToEstimate>>, { success: true }>["data"]
>;

export type QtoReviewSubmitOptions = LineItemsCsvOptions;

/** Increments whenever the shared "QTO Review" worksheet is (re)built so other integration UIs can drop stale submit state. */
let qtoReviewSheetGeneration = 0;
const qtoReviewSheetGenerationListeners = new Set<() => void>();

function bumpQtoReviewSheetGeneration(): void {
  qtoReviewSheetGeneration += 1;
  qtoReviewSheetGenerationListeners.forEach((listener) => listener());
}

export function getQtoReviewSheetGeneration(): number {
  return qtoReviewSheetGeneration;
}

export function subscribeQtoReviewSheetGeneration(onStoreChange: () => void): () => void {
  qtoReviewSheetGenerationListeners.add(onStoreChange);
  return () => qtoReviewSheetGenerationListeners.delete(onStoreChange);
}

export function useQtoReviewSheetGeneration(): number {
  return useSyncExternalStore(
    subscribeQtoReviewSheetGeneration,
    getQtoReviewSheetGeneration,
    getQtoReviewSheetGeneration
  );
}

/**
 * Top-level QTO Review / Submit actions: owns Logger.error once per failure path per docs/error-handling-and-logging.md.
 */
/**
 * Runs a take-off workflow inside the hooks layer's logging boundary, so a component-level flow is
 * still Sentry-eligible. Logs once and rethrows; the caller decides what the user sees.
 */
export const withQtoLogging = async (callback: () => Promise<void>, loggingInfo?: LoggingInfo): Promise<void> => {
  try {
    await callback();
  } catch (error) {
    const err = handleError(error, { context: loggingInfo?.context || "withQtoLogging", ...loggingInfo });
    Logger.error(loggingInfo?.message || "Take-off action failed", err);
    throw err;
  }
};

export function useQtoReviewActions() {
  const createQtoReviewSheet = async (aggregated: AggregatedQto[], estimateId: string | null): Promise<void> => {
    let lineItems: EstimateLineItemForReview[] = [];
    if (estimateId) {
      // Force a fresh export for the review build (main fetched fresh every time): matching
      // against a stale cached copy produces wrong add/update decisions, which is worse than failing.
      // Uncached — this reads line items for review matching, it does not "load" the estimate.
      const exportResult = await fetchEstimateExportUncached(estimateId);
      // An empty-success payload is as unusable as a failure here: matching against [] would
      // classify every existing line item as "Add" and import duplicates
      if (!exportResult.success || !exportResult.data) {
        const raw = exportResult.success ? new Error("Estimate export returned no data") : exportResult.error;
        const err = handleError(raw, {
          context: "fetchEstimateLineItemsForQtoReview",
          estimateId,
        });
        Logger.error("Failed to load estimate line items for QTO review", err, { estimateId });
        throw err;
      }
      lineItems = exportResult.data?.line_items ?? [];
    }

    try {
      await reviewQTO(aggregated, lineItems);
      bumpQtoReviewSheetGeneration();
    } catch (error) {
      const err = handleError(error, { context: "reviewQTO" });
      Logger.error("Failed to create QTO Review sheet", err);
      throw err;
    }
  };

  const submitQtoReviewToEstimate = async (
    estimateId: string,
    submitOptions: QtoReviewSubmitOptions
  ): Promise<SubmitSuccessData> => {
    let result: Awaited<ReturnType<typeof exportReviewToEstimate>>;
    try {
      result = await exportReviewToEstimate(estimateId, {
        qtoSourceId: submitOptions.qtoSourceId,
        importBatch: submitOptions.importBatch,
        updateImportBatch: submitOptions.updateImportBatch,
        addFilename: submitOptions.addFilename,
        updateFilename: submitOptions.updateFilename,
      });
    } catch (error) {
      const err = handleError(error, { context: "exportReviewToEstimate", estimateId });
      Logger.error("Failed to submit QTO review to estimate", err, { estimateId });
      throw err;
    }
    if (!result.success) {
      const raw = result.error ?? new Error("exportReviewToEstimate failed");
      const err = handleError(raw, { context: "exportReviewToEstimate", estimateId });
      Logger.error("Failed to import QTO review line items", err, { estimateId });
      throw err;
    }
    // The import changed the estimate's line items, so swap in a fresh export. If this refresh
    // fails, the staleness check catches report runs and the next estimate refresh picks it up.
    await refreshEstimateExportCache(estimateId);
    clearProcessedEstimateCache();
    const source = submitOptions.qtoSourceId;
    const data = result.data ?? {
      rowCount: 0,
      addRowCount: 0,
      updateRowCount: 0,
    };
    if (data.addRowCount > 0) {
      void track(`excel.qto.${source}.import`, {
        estimateId,
        rowCount: data.addRowCount,
      });
    }
    if (data.updateRowCount > 0) {
      void track(`excel.qto.${source}.update`, {
        estimateId,
        rowCount: data.updateRowCount,
      });
    }
    return data;
  };

  return { createQtoReviewSheet, submitQtoReviewToEstimate };
}

export function useQtoReviewRowCounts(estimateId: string | null | undefined, aggregated: AggregatedQto[] | null) {
  const [lineItems, setLineItems] = useState<EstimateLineItemForReview[] | null>(null);
  const [loadingLineItems, setLoadingLineItems] = useState(false);
  const [lineItemsError, setLineItemsError] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);

  const counts = useMemo(() => {
    if (aggregated == null) return null;
    if (!estimateId) {
      return countQtoReviewRows(aggregated, []);
    }
    if (lineItemsError || lineItems === null || loadingLineItems) return null;
    return countQtoReviewRows(aggregated, lineItems);
  }, [aggregated, estimateId, lineItems, lineItemsError, loadingLineItems]);

  /** True before the first effect run and during fetch when an estimate is selected (state starts with lineItems null, loading false). */
  const awaitingLineItemsForEstimate = !!estimateId && lineItems === null && !lineItemsError;

  useEffect(() => {
    if (!estimateId) {
      setLineItems(null);
      setLoadingLineItems(false);
      setLineItemsError(false);
      return;
    }

    let cancelled = false;
    setLineItems(null);
    setLoadingLineItems(true);
    setLineItemsError(false);

    // Fresh fetch (main parity) — counts gate the Review button and must match the fresh
    // export the review sheet is built from, not a possibly hours-old cached copy. Uncached: this reads
    // line items for the count, it does not "load" the estimate.
    void fetchEstimateExportUncached(estimateId).then((result) => {
      if (cancelled) return;
      setLoadingLineItems(false);
      if (result.success && result.data) {
        setLineItems((result.data.line_items ?? []) as EstimateLineItemForReview[]);
      } else {
        const raw = result.success ? new Error("Estimate export returned no data") : result.error;
        const err = handleError(raw, {
          context: "fetchEstimateLineItemsForQtoRowCounts",
          estimateId,
        });
        Logger.error("Failed to load estimate line items for QTO row counts", err, { estimateId });
        setLineItems(null);
        setLineItemsError(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [estimateId, reloadVersion]);

  const refreshLineItems = useCallback(() => {
    setReloadVersion((v) => v + 1);
  }, []);

  return {
    counts,
    loadingLineItems: loadingLineItems || awaitingLineItemsForEstimate,
    lineItemsError,
    refreshLineItems,
  };
}
